import * as React from "react"
import { areEqual } from "@digits-shared/helpers/arrayHelper"

/**
 * Hook that returns a stable array reference when the values are identical.
 * This prevents unnecessary recalculations in dependent useMemo hooks.
 *
 * Note: This hook uses strict equality comparison (`!==`). For arrays containing objects,
 * it will only return the same instance if the exact same array reference is passed,
 * or if all elements are primitives with identical values.
 *
 * @param array - The array to stabilize
 * @returns The same array instance if values are identical, or a new instance if values changed
 *
 * @example
 * ```tsx
 * // For primitive arrays - returns same instance when values are identical
 * const stableIndices = useStableArray(virtualRows.map(v => v.index))
 *
 * // For object arrays - returns same instance only when same reference is passed
 * const stableObjects = useStableArray([{ id: 1 }, { id: 2 }])
 *
 * const expensiveCalculation = useMemo(() => {
 *   // This will only recalculate when the array reference actually changes
 *   return stableIndices.map(/* expensive operation *\/)
 * }, [stableIndices])
 * ```
 */
export function useStableArray<T>(array: T[]): T[] {
  const prevArrayRef = React.useRef<T[]>(null)

  return React.useMemo(() => {
    // If this is the first render or arrays have different content, return the new array
    if (!prevArrayRef.current || !areEqual(prevArrayRef.current, array)) {
      prevArrayRef.current = array
      return array
    }

    // If arrays have the same content, return the previous instance to maintain reference stability
    return prevArrayRef.current
  }, [array])
}
